import * as R from 'ramda';

import type { Filters } from '../FiltersContext';
import type { IEvent } from '../../';

const hasSelectedCities = (filters) => filters.selectedCities?.length > 0;
const hasSelectedCountries = (filters) => filters.selectedCountries?.length > 0;
const hasSelectedStates = (filters) => filters.selectedStates?.length > 0;

export const canFilterByCountry = hasSelectedCountries;
export const canFilterByState = R.allPass([hasSelectedStates, hasSelectedCountries]);
export const canFilterByCity = R.allPass([hasSelectedCities, hasSelectedStates, hasSelectedCountries]);

export const filterByCountry = (filters: Filters) => (event: IEvent) =>
	canFilterByCountry(filters) ? filters.selectedCountries.includes(event.country) : true;

export const filterByState = (filters: Filters, country?: string) => (event: IEvent) => {
	if (!hasSelectedStates(filters)) return true;

	const hasCountry = hasSelectedCountries(filters) || !!country;

	return hasCountry ? filters.selectedStates.includes(event.state) : true;
};

export const filterByCity = (filters: Filters, country?: string, state?: string) => (event: IEvent) => {
	if (!hasSelectedCities(filters)) return true;

	const hasCountry = hasSelectedCountries(filters) || !!country;
	const hasState = hasSelectedStates(filters) || !!state;

	return hasCountry && hasState ? filters.selectedCities.includes(event.city) : true;
};

/**
 * Generic location filter function for shortcode/data-attribute filtering
 * @param filter - The location value from shortcode/embedding script data-attribute (single value or comma-separated)
 * @param property - The property name on the event object ('country', 'state', or 'city')
 * @returns A filter function that checks if the event matches the location criteria
 */
export const filterByShortcodeLocation =
	(filters?: string[], property?: 'country' | 'state' | 'city') =>
	(event: IEvent): boolean => {
		// If no filter or property specified, don't filter
		if (filters.length === 0 || !property) return true;

		// Get the event's property value
		const eventValue = event[property];
		if (!eventValue) return false;

		return filters.some((v) => eventValue.toLocaleLowerCase() === v.toLocaleLowerCase());
	};
